import React, { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import "./LiveSessionTestPage.css";

const COLLECTION = "liveSessionTest";
const MAX_ITEMS = 100;

function formatTimestamp(value) {
  if (!value) return "—";
  const date = value.toDate ? value.toDate() : new Date(value);
  return date.toISOString();
}

const LiveSessionTestPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (docId) => {
    if (!docId || deletingId) return;
    setDeletingId(docId);
    try {
      const fn = httpsCallable(getFunctions(), "liveSessionTestDelete");
      await fn({ docId });
      setError(null);
    } catch (err) {
      console.error("LiveSessionTest delete error:", err);
      setError(err?.message || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const q = query(
      collection(db, COLLECTION),
      orderBy("timestamp", "desc"),
      limit(MAX_ITEMS)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          const { timestamp, ...payload } = data;
          return {
            id: docSnap.id,
            timestamp: timestamp || null,
            payload,
          };
        });
        setEntries(list);
        setError(null);
      },
      (err) => {
        console.error("LiveSessionTest snapshot error:", err);
        setError(err?.message || "Failed to load data");
      }
    );
    setLoading(false);
    return () => unsubscribe();
  }, []);

  return (
    <div className="live-session-test-page">
      <h1>Live Session Test (Device Data)</h1>
      <p className="live-session-test-description">
        Test payloads sent from devices in the field. Data is stored in Firestore with a server timestamp.
      </p>

      <details className="live-session-test-howto" style={{ marginBottom: 24 }}>
        <summary>How to test</summary>
        <p style={{ marginTop: 8 }}>
          POST to the Cloud Function endpoint with a JSON body. Example:
        </p>
        <pre style={{
          padding: 12,
          background: 'rgba(0,0,0,0.3)',
          borderRadius: 8,
          overflow: 'auto',
          fontSize: 13
        }}>{`curl -X POST \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_DEVICE_TOKEN" \\
  -H "X-Device-Id: esp32-A1B2C3D4" \\
  -d '{"temperature": 22.5, "deviceId": "esp32-A1B2C3D4"}' \\
  https://us-central1-victorise-video-tool.cloudfunctions.net/liveSessionTestIngest`}</pre>
      </details>

      {error && (
        <div className="live-session-test-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p className="live-session-test-loading">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="live-session-test-empty">No test data yet.</p>
      ) : (
        <div className="live-session-test-list">
          {entries.map((entry) => (
            <div key={entry.id} className="live-session-test-card">
              <div className="live-session-test-card-header">
                <span className="live-session-test-id">{entry.id}</span>
                <div className="live-session-test-card-actions">
                  <span className="live-session-test-time">
                    {formatTimestamp(entry.timestamp)}
                  </span>
                  <button
                    type="button"
                    className="live-session-test-delete"
                    onClick={() => handleDelete(entry.id)}
                    disabled={deletingId === entry.id}
                    aria-label={`Delete ${entry.id}`}
                  >
                    {deletingId === entry.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
              <pre className="live-session-test-payload">
                {JSON.stringify(entry.payload, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveSessionTestPage;
